import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, FormControlLabel,
  Switch, Snackbar, Alert, CircularProgress, Divider, Stack, Avatar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { useNavigate, useParams } from 'react-router-dom';
import { salesExecutiveApi } from '../api/apiClient';
import PageShell from '../common/PageShell';

const emptyForm = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: "",
  mobileNumber: '',
  experience: '',
  active: true,
  address: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
  },
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, '& svg': { fontSize: 18 } }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

const SalesExecutiveForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (isEdit) {
      salesExecutiveApi
        .getById(id)
        .then((res) => {
          setForm({
            ...emptyForm,
            ...res.data,
            address: { ...emptyForm.address, ...(res.data.address || {}) },
          });
        })
        .catch(() => {
          setSnackbar({ open: true, message: 'Failed to load', severity: 'error' });
        });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, address: { ...form.address, [name]: value } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) await salesExecutiveApi.update(id, form);
      else await salesExecutiveApi.create(form);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
      setTimeout(() => navigate('/sales-executives'), 1000);
    } catch {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      icon={isEdit ? <EditIcon /> : <AddCircleIcon />}
      title={isEdit ? 'Edit Sales Executive' : 'New Sales Executive'}
      subtitle={
        isEdit
          ? 'Update personal and contact information'
          : 'Add a new member to your sales team'
      }
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Sales Executives', to: '/sales-executives' },
        { label: isEdit ? 'Edit' : 'New' },
      ]}
      actions={
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales-executives')}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
          variant="outlined"
        >
          Back
        </Button>
      }
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Personal info */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<PersonIcon />}
                  title="Personal Information"
                  subtitle="Name, contact and experience"
                />
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      name="middleName"
                      value={form.middleName}
                      onChange={handleChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="mobileNumber"
                      value={form.mobileNumber}
                      onChange={handleChange}
                      required
                      size="small"
                      placeholder="e.g. +91 98765 43210"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Experience (years)"
                      name="experience"
                      value={form.experience}
                      onChange={handleChange}
                      size="small"
                      type="number"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<HomeIcon />}
                  title="Address"
                  subtitle="Optional contact address"
                />
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      name="addressLine1"
                      value={form.address.addressLine1}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      name="addressLine2"
                      value={form.address.addressLine2}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={form.address.city}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={form.address.state}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={form.address.country}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      name="pincode"
                      value={form.address.pincode}
                      onChange={handleAddressChange}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<ToggleOnIcon />}
                  title="Status"
                  subtitle="Set record visibility"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.active}
                      name="active"
                      onChange={handleChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {form.active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.active
                          ? 'Executive is available for assignment'
                          : 'Executive is hidden from active use'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Stack>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/sales-executives')}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={!loading && <SaveIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isEdit ? (
                    'Update Executive'
                  ) : (
                    'Create Executive'
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default SalesExecutiveForm;